import React, { createContext, useState, useEffect, useContext, useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { CODE_SNIPPETS } from './CodeEditorComponent/constants';
import {toast} from 'react-hot-toast';
import { set } from 'lodash';
import useSentryLogger from './hooks/useSentryLogger';
import mobilePlaceImage from './assets/place_mobile.jpeg';
import placeLaptop from './assets/proctoring/laptop.png';
import placeMobile from './assets/proctoring/mobile.png';

import logger from './utils/logger.js';



const WebSocketContext = createContext();

export const useWebSocket = () => {
  return useContext(WebSocketContext);
};

export const WebSocketProvider = ({ children }) => {
  // Get Sentry logger
  const {
    logWebSocketEvent,
    logWebSocketError,
    logNetworkEvent,
    logNetworkError,
    logInterviewEvent,
    logPermissionEvent
  } = useSentryLogger();

  const [ws, setWs] = useState(null);
  const [socketData, setsocketData] = useState({isNavigate:false, room_url:''})
  const [wsMessageEvent, setWsMessageEvent] = useState(null);
  const [evaSpeaking, setEvaSpeaking] = useState(false);
  const [transcriptData, setTranscriptData] = useState([]);
  const [aiTranscript, setAiTranscript] = useState('');
  const [question, setQuestion] = useState('');
  const [showEditor, setShowEditor] = useState(false);
  const [typing, setTyping] = useState('');
  const [editorContent, setEditorContent] = useState(CODE_SNIPPETS.javascript);
  const BASE_URL = import.meta.env.VITE_APP_BASE_URL;
  const interviewId = window.location.href.split("/")[4];
  const [id, setId] = useState("");
  const [isTabVisible, setIsTabVisible] = useState(true);
  const [interviewCategory, setInterviewCategory] = useState(() => {
    // Initialize from localStorage if available
    return localStorage.getItem("interviewCategory") || '';
  });
  const [isCodeEditorEnabled, setIsCodeEditorEnabled] = useState(() => {
    // Initialize based on interviewCategory from localStorage
    const storedCategory = localStorage.getItem("interviewCategory");
    // For backward compatibility, assume TECH if no category is stored but we're in an interview
    const inInterview = localStorage.getItem("job_interview_id") !== null;
    return storedCategory === 'TECH' || (inInterview && !storedCategory);
  });
  const [isConnected, setIsConnected] = useState(false);
  const interview_id = localStorage.getItem("interview_id")
  const [prevCameraState, setPrevCameraState] = useState(null);
  const [prevMicState, setPrevMicState] = useState(null);
  const permissionChangeTimeout = useRef(null);
  const [reactCodingQuestion, setReactCodingQuestion] = useState(false)
  const [codeLanguage, setCodeLanguage] = useState('java');
  const [reconnectAttempts, setReconnectAttempts] = useState(0);
  const [reconnectionStatus, setReconnectionStatus] = useState('loading'); // 'loading', 'connected', 'connecting', 'max_attempts_reached'
  const [isProctoringEnabled, setProctoringEnabled] = useState(false)
  const [isProctoringResetup, setProctoringResetup] = useState(false)
  const [isSecondryCameraJoined, setSecondryCameraJoined] = useState(false)
  const [questionTimer, setQuestionTimer] = useState(null);
  const [showExcalidraw, setShowExcalidraw] = useState(false);
  const [pendingEvents, setPendingEvents] = useState([]);
  const [queuedEventTypes, setQueuedEventTypes] = useState(new Set());

  // Add separate queues for permission events
  const [cameraPermissionEvent, setCameraPermissionEvent] = useState(null);
  const [microphonePermissionEvent, setMicrophonePermissionEvent] = useState(null);
  const [botInteractionEvent, setBotInteractionEvent] = useState(null);

  const [cameraPermissionEventSent, setCameraPermissionEventSent] = useState(false);
  const [microphonePermissionEventSent, setMicrophonePermissionEventSent] = useState(false);
  const [botInteractionEventSent, setBotInteractionEventSent] = useState(false);

  const [isMCQComponentVisible, setMCQComponentVisible] = useState(false);
  const [isMCQDataAvailable, setMCQDataAvailable] = useState(null)
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(null);
  const [totalQuestions, setTotalQuestions] = useState(null);
  const [boilerplateCode, setBoilerplateCode] = useState(null);

  // Add state for entity detection popup
  const [showEntityDetectionPopup, setShowEntityDetectionPopup] = useState(false);
  
  // Add state for image carousel
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  const [hintsUsed, setHintsUsed] = useState(() => {
    const jobInterviewId = localStorage.getItem("job_interview_id");
    // Use interview-specific key for hint tracking
    const savedHints = localStorage.getItem(`interview_hints_used_${jobInterviewId}`);
    return savedHints ? parseInt(savedHints, 10) : 0;
  });
  const MAX_HINTS_PER_INTERVIEW = 3;

  // Keep-alive timer for coding questions
  const keepAliveIntervalRef = useRef(null);

  const maxReconnectAttempts = 3; // Changed to 3 as per requirements
  const reconnectTimeoutRef = useRef(null);
  const [isOnline, setIsOnline] = useState(navigator.onLine);

  const [proctoringStepsCompleted, setProctoringStepsCompleted] = useState({
    qrScanned: false,
    deviceJoined: false,
    roomScanned: false,
    cameraPlaced: false
  });

  const queueConnectionEvent = (eventType, eventData) => {
    // Handle special permission events separately
    if (eventData.message === 'camera_permission_success') {
      setCameraPermissionEvent(eventData);
      logPermissionEvent('camera permission queued', {
        interview_id: interviewId
      });
      return false;
    }
    if (eventData.message === 'microphone_permission_success') {
      setMicrophonePermissionEvent(eventData);
      logPermissionEvent('microphone permission queued', {
        interview_id: interviewId
      });
      return false;
    }
    if (eventData.message === 'bot_interaction_permission_success') {
      setBotInteractionEvent(eventData);
      logPermissionEvent('bot interaction permission queued', {
        interview_id: interviewId
      });
      return false;
    }

    // Check if this is a special event type that should not be duplicated
    const specialEventTypes = [
      'websocket_disconnected'
    ];

    // If this is a special event type, check if it's already queued
    if (specialEventTypes.includes(eventData.message)) {
      if (queuedEventTypes.has(eventData.message)) {
        // logger.debug(`Skipping duplicate ${eventData.message} event`);
        logWebSocketEvent('duplicate event skipped', {
          eventType: eventData.message,
          interview_id: interviewId
        });
        return false;
      }
      // Add to tracked event types
      setQueuedEventTypes(prev => new Set([...prev, eventData.message]));
    }

    // Queue the event if WebSocket is not connected
    if (!ws || ws.readyState !== WebSocket.OPEN) {
      // logger.debug(`Queuing ${eventType} event for later sending: `, eventData);
      logWebSocketEvent('event queued', {
        eventType,
        message: eventData.message,
        interview_id: interviewId
      });
      setPendingEvents(prev => [...prev, { type: eventType, data: eventData, timestamp: Date.now() }]);
      return false;
    }
    return true;
  };

  const sendConnectionStatusEvent = (status) => {
    // Only send websocket disconnected and reconnected events
    if (status === 'disconnected' || status === 'reconnected') {
      const event = {
        type: "client_event",
        message: `websocket_${status}`,
        timestamp: new Date().toISOString()
      };

      if (ws && ws.readyState === WebSocket.OPEN) {
        // logger.debug(`Sending websocket ${status} event`);
        logWebSocketEvent(`connection ${status}`, {
          interview_id: interviewId
        });
        ws.send(JSON.stringify(event));
        return true;
      } else {
        // logger.debug(`Queuing websocket ${status} event`);
        logWebSocketEvent(`connection ${status} queued`, {
          interview_id: interviewId
        });
        queueConnectionEvent("websocket_status", event);
        return false;
      }
    }
    return false;
  };

  // Update the processPendingEvents function to handle permission events
  const processPendingEvents = useCallback(() => {
    if (ws && ws.readyState === WebSocket.OPEN) {
      const eventsToSend = [...pendingEvents];
      
      // Add permission events if they exist
      if (cameraPermissionEvent) {
        eventsToSend.push({ type: "client_event", data: cameraPermissionEvent, timestamp: Date.now() });
        setCameraPermissionEvent(null);
      }
      if (microphonePermissionEvent) {
        eventsToSend.push({ type: "client_event", data: microphonePermissionEvent, timestamp: Date.now() });
        setMicrophonePermissionEvent(null);
      }
      if (botInteractionEvent) {
        eventsToSend.push({ type: "client_event", data: botInteractionEvent, timestamp: Date.now() });
        setBotInteractionEvent(null);
      }

      if (eventsToSend.length > 0) {
        // logger.debug(`Processing ${eventsToSend.length} pending events`);
        logWebSocketEvent('processing pending events batch', {
          batchSize: eventsToSend.length,
          interview_id: interviewId
        });
        
        // Create a batch event with all pending events
        const batchEvent = {
          type: "client_event",
          message: "connection_event_batch",
          events: eventsToSend,
          current_status: "connected"
        };
        
        // Send the batch event
        ws.send(JSON.stringify(batchEvent));
        
        // Clear the pending events queue and tracked event types
        setPendingEvents([]);
        setQueuedEventTypes(new Set());
        return true;
      }
    }
    return false;
  }, [ws, pendingEvents, cameraPermissionEvent, microphonePermissionEvent, botInteractionEvent]);

  // Update the sendPermissionSuccessEvent function
  const sendPermissionSuccessEvent = (permissionType) => {
    const event = {
      type: "client_event",
      message: `${permissionType}_permission_success`,
      permission: permissionType,
      timestamp: new Date().toISOString()
    };

    // Try to send immediately if connected
    if (ws && ws.readyState === WebSocket.OPEN) {
      // logger.debug(`Sending ${permissionType} permission success event`);
      logPermissionEvent(`${permissionType} permission success sent`, {
        interview_id: interviewId
      });
      ws.send(JSON.stringify(event));
      // logger.debug("permission success event sent");
      return true;
    } else {
      // Queue the event using the new queueing system
      logPermissionEvent(`${permissionType} permission success queued`, {
        interview_id: interviewId
      });
      queueConnectionEvent("permission_event", event);
      return false;
    }
  };

  // New direct permission event sender functions to be passed as props
  const sendCameraPermissionSuccess = () => {
    return sendPermissionSuccessEvent('camera');
  };

  const sendMicrophonePermissionSuccess = () => {
    return sendPermissionSuccessEvent('microphone');
  };

  const sendBotInteractionSuccess = () => {
    return sendPermissionSuccessEvent('bot_interaction');
  };

  // Add face detection event handlers
 
  const sendNoFaceDetectedEvent = useCallback(() => {
    const event = {
      type: "client_event",
      message: "no_face_detected",
      status: "no_face_detected",
      timestamp: new Date().toISOString()
    };

    // Try to send immediately if connected
    if (ws && ws.readyState === WebSocket.OPEN) {
      // logger.debug('Sending no face detected event');
      ws.send(JSON.stringify(event));
      // logger.debug("no face detected event sent")    
      return true;
    } else {
      // Otherwise queue it
      // logger.debug('Queuing no face detected event');
      queueConnectionEvent("face_detection", event);
      return false;
    }
  }, [ws]);

  const sendMultipleFacesDetectedEvent = useCallback((faceCount) => {
    const event = {
      type: "client_event",
      message: "multiple_faces_detected",
      status: "multiple_faces_detected",
      count: faceCount || 2,
      timestamp: new Date().toISOString()
    };

    // Try to send immediately if connected
    if (ws && ws.readyState === WebSocket.OPEN) {
      // logger.debug(`Sending multiple faces (${faceCount}) detected event`);
      ws.send(JSON.stringify(event));
      // logger.debug("multiple faces detected event sent")
      return true;
    } else {
      // Otherwise queue it
      // logger.debug(`Queuing multiple faces (${faceCount}) detected event`);
      queueConnectionEvent("face_detection", event);
      return false;
    }
  }, [ws]);

  // Define the connectWebSocket function for reconnection
  const connectWebSocket = useCallback(() => {
    // If this is the first connection attempt, keep the loading state
    // If it's a reconnection attempt, set to connecting state
    if (reconnectionStatus !== 'loading') {
      setReconnectionStatus('connecting');
    }
    // Get the interview ID from URL or localStorage
    const urlId = id || window.location.href.split("/")[4];
    const storedId = localStorage.getItem("job_interview_id");
    const interviewId = urlId || storedId;
    
    if (!interviewId) {
      // logger.error("No interview ID available for WebSocket reconnection");
      logWebSocketError(new Error("No interview ID available for WebSocket reconnection"), {
        tags: { action: 'reconnection_attempt' }
      });
      return;
    }
    
    logWebSocketEvent('manual reconnection initiated', {
      interview_id: interviewId,
      reconnection_status: reconnectionStatus
    });
    
    const newWs = new WebSocket(`wss://${BASE_URL}/api/interview/interview_transcript_ws_v2`);
    
    newWs.onopen = () => {

      logWebSocketEvent('reconnection successful', {
        interview_id: interviewId,
        reconnect_attempts_used: reconnectAttempts
      });
      setIsConnected(true);
      setReconnectionStatus('connected');
      newWs.send(JSON.stringify({
        "interview_id": interviewId
      }));
      // logger.debug('WebSocket Reconnected with ID:', interviewId);
      setReconnectAttempts(0); // Reset reconnect attempts on successful connection
      toast.success('Connection restored successfully!', {
        id: 'reconnection-toast'
      });
      
      // Process any pending events after reconnection
      setTimeout(() => {
        sendConnectionStatusEvent("reconnected");
        processPendingEvents();
      }, 500);
    };
    
    // Set up the same event handlers as the initial connection
    if (ws) {
      newWs.onmessage = ws.onmessage;
      newWs.onerror = ws.onerror;
      newWs.onclose = ws.onclose;
    }
    
    setWs(newWs);
  }, [id, BASE_URL, ws, processPendingEvents]);

    // Manual retry function that can be called from UI
    const manualRetryConnection = useCallback(() => {
      logWebSocketEvent('manual retry connection requested', {
        interview_id: interviewId
      });
      setReconnectionStatus('connecting');
      setReconnectAttempts(0);
      connectWebSocket();
      toast.success('Attempting to reconnect...');
    }, [connectWebSocket]);

  // Add a helper function for language key conversion
  const getLanguageKeyForBoilerplate = (languageCode) => {
    if (!languageCode) return 'Java'; // Default to Java
    
    const lowercased = languageCode.toLowerCase();
    
    // Special case for SQL
    if (lowercased === 'sql') {
      return 'SQL';
    }
    
    // Map other language codes to their proper keys
    const keyMap = {
      'javascript': 'JavaScript',
      'python': 'Python',
      'cpp': 'C++',
      'c++': 'C++',
      'java': 'Java'
    };
    
    return keyMap[lowercased] || languageCode.charAt(0).toUpperCase() + lowercased.slice(1);
  };

  // logger.debug(codeLanguage,"codeLanguage===>")
  // logger.debug("question id", window.location.href.split("/")[4])
  // logger.debug("socket data", socketData)


  const handleSubmit = (e) => {
    const loader = toast.loading('Loading...')
    const formData = new FormData();
    formData.append("job_interview_id", interviewId);
    fetch(`https://${BASE_URL}/api/video-interview_v2`, {
      method: "POST",
      body: formData,
    })
      .then((response) => {
        return response.json();
      })
      .then((data) => {
        localStorage.setItem("interview_id", id)
        // logger.debug("Success:=>", data);
      })
      .catch((error) => {
        // logger.error("Error:=>", error.message);
      }).finally(()=>{
        setTimeout(() => {
          toast.remove(loader)
        }, 5000);
      })
  };
  
  const sendPermissionChangeEvent = (deviceType, isEnabled) => {
    // Clear any pending timeout
    if (permissionChangeTimeout.current) {
      clearTimeout(permissionChangeTimeout.current);
    }

    // Debounce the permission change event
    permissionChangeTimeout.current = setTimeout(() => {
      if (ws && ws.readyState === WebSocket.OPEN) {
        // Only send if the state has actually changed
        if ((deviceType === 'camera' && prevCameraState !== isEnabled) ||
            (deviceType === 'microphone' && prevMicState !== isEnabled)) {
          
          const message = {
            type: "client_event",
            message: `${deviceType === 'camera' ? 'Camera' : 'Microphone'} permission_changed`,
            device: deviceType,
            enabled: isEnabled
          };

          ws.send(JSON.stringify(message));

          // Update the state after sending
          if (deviceType === 'camera') {
            setPrevCameraState(isEnabled);
          } else {
            setPrevMicState(isEnabled);
          }
        }
      }
    }, 300); // 300ms debounce
  };

  const handlePermissionChange = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: true,
        audio: true
      });

      // Initial state setup
      const videoTrack = stream.getVideoTracks()[0];
      const audioTrack = stream.getAudioTracks()[0];
      
      setPrevCameraState(videoTrack?.enabled);
      setPrevMicState(audioTrack?.enabled);

      // Set up permission checks with reduced frequency
      const permissionCheckInterval = setInterval(() => {
        if (ws?.readyState === WebSocket.OPEN) {
          const tracks = stream.getTracks();
          tracks.forEach(track => {
            const isEnabled = track.enabled && track.readyState === 'live';
            
            if (track.kind === 'video' && prevCameraState !== isEnabled) {
              sendPermissionChangeEvent('camera', isEnabled);
            } else if (track.kind === 'audio' && prevMicState !== isEnabled) {
              sendPermissionChangeEvent('microphone', isEnabled);
            }
          });
        }
      }, 1000);

      return () => {
        if (permissionCheckInterval) {
          clearInterval(permissionCheckInterval);
        }
        if (permissionChangeTimeout.current) {
          clearTimeout(permissionChangeTimeout.current);
        }
        stream.getTracks().forEach(track => track.stop());
      };
    } catch (error) {
      // logger.error('Error setting up permission listeners:', error);
    }
  };

  const sendScreenshotEvent = () => {
    if (ws && ws.readyState === WebSocket.OPEN) {
      const message = {
        type: "client_event",
        message: "screenshot_attempted"
      };
      ws.send(JSON.stringify(message));
      // logger.debug('Screenshot event sent successfully');
    } else {
      // logger.debug('Screenshot event not sent - WebSocket not connected');
    }
  };

  const sendFullscreenExitEvent = () => {
    if (ws && ws.readyState === WebSocket.OPEN) {
      const message = {
        type: "client_event",
        message: "fullscreen_exit_attempted"
      };
      ws.send(JSON.stringify(message));
      // logger.debug('Fullscreen exit event sent successfully');
    } else {
      // logger.debug('Fullscreen exit event not sent - WebSocket not connected');
    }
  };

  const sendTabCloseEvent = () => {
    if (ws && ws.readyState === WebSocket.OPEN) {
      const event = {
        type: "client_event",
        event_type: "tab_close"
      };
      ws.send(JSON.stringify(event));
    } else {
      // logger.debug('Tab close event not sent - WebSocket not connected');
    }
  };

  useEffect(() => {
    // logger.debug(id,"id==>")
    // Get the interview ID from URL or localStorage
    const urlId = id || window.location.href.split("/")[4];
    const storedId = localStorage.getItem("job_interview_id");
    const interviewId = urlId || storedId;
    
    // logger.debug("Connecting WebSocket with ID:", interviewId);
    logWebSocketEvent('connection attempt', {
      interview_id: interviewId,
      url: urlId,
      stored: storedId
    });
    
    if (!interviewId) {
      // logger.error("No interview ID available for WebSocket connection");
      logWebSocketError(new Error("No interview ID available for WebSocket connection"), {
        tags: { action: 'connection_init' }
      });
      return;
    }
    
    const ws = new WebSocket(`wss://${BASE_URL}/api/interview/interview_transcript_ws_v2`);
    let permissionCheckInterval;  // Declare interval variable

    ws.onopen = () => {
      // logger.debug("WebSocket connection established");
      logWebSocketEvent('connection established', {
        interview_id: interviewId,
        url: `wss://${BASE_URL}/api/interview/interview_transcript_ws_v2`
      });
      setIsConnected(true);
      setReconnectionStatus('connected'); // Update status to connected
      ws.send(JSON.stringify({
        "interview_id": interviewId
      }));
      // logger.debug('WebSocket Connected with ID:', interviewId);
      
      // Send connection status after connection established
      setTimeout(() => {
        sendConnectionStatusEvent("connected");
      }, 300);
    };

    ws.onmessage = (event) => {

      logger.debug('🔄 WebSocket Message Received:', event.data);
      const result = event.data;
      const res = JSON.parse(result);
      logger.debug("📦 Parsed WebSocket Data:", {

        type: res?.type,
        message: res?.message,
        fullData: res
      });
      setWsMessageEvent(res);

      // Log significant message types
      if (res?.type) {
        logWebSocketEvent('message received', {
          messageType: res.type,
          interview_id: interviewId
        });
      }

      if (res?.type === "side_camera_left") {
        // logger.debug("Side camera data received:", res);
        logInterviewEvent('side camera left', {
          interview_id: interviewId,
          side_camera_url: res?.side_camera_url
        });
        setProctoringEnabled(true);

        setsocketData({
          ...socketData,
          side_camera_url:res?.side_camera_url
          
        })
        sentryLogger.info("Side camera left", {
          interview_id: interviewId,
          side_camera_url: res?.side_camera_url
        });
      }

      if (res?.type === "show_qr_code") {
        logInterviewEvent('QR code shown', {
          interview_id: interviewId,
          side_camera_url: res?.side_camera_url
        });
        setsocketData({
          ...socketData,
          side_camera_url:res?.side_camera_url
        })
        
        setProctoringEnabled(true);
        setProctoringResetup(true);
      }   
        
       // ⭐ Progress tracking setup
  if (["question", "coding_question", "react_coding_question", "mcq_question", "hld_question"].includes(res?.type)) {
  
    if (res?.current_question_index !== undefined) {
      // logger.debug("📋 Current Question Number:", res.current_question_index);
      logInterviewEvent('question progress updated', {
        interview_id: interviewId,
        current_question: res.current_question_index,
        question_type: res.type
      });
      setCurrentQuestionIndex(res.current_question_index);
    }
    if (res?.total_questions !== undefined) {
      // logger.debug("📋 Total Questions:", res.total_questions);
      setTotalQuestions(res.total_questions);
    }
  }

     
      // Set the ID from the parsed response if it exists
      if (res?.id) {
        setId(res.id);
      }
      
      if(res?.type==="interview_status"){
        if(res?.message==="not_running"){
          logInterviewEvent('interview not running', {
            interview_id: interviewId
          });
          setsocketData({isNavigate:false, canJoin:true})
        }else{
          logInterviewEvent('interview status received', {
            interview_id: interviewId,
            room_url: res?.room_url,
            user_name: res?.user_name,
            interview_category: res?.interview_category
          });
          setsocketData({isNavigate:true, room_url:res?.room_url, user_name:res?.user_name})
          setInterviewCategoryWithStorage(res?.interview_category || '');
          // logger.debug("Setting interview category:", res?.interview_category);
        }
      }
      if (res?.type === "bot_started") {
        // Get the language from the response, convert to lowercase for consistency
        const language = res?.code_language?.toLowerCase() || 'java';
        
        logInterviewEvent('bot started', {
          interview_id: interviewId,
          code_language: language,
          interview_category: res?.interview_category,
          proctoring_enabled: res?.proctoring_enabled
        });
        
        // If selecting SQL, set SQL mode to true
        if (res?.code_language?.toLowerCase() === 'sql') {
          // logger.debug("SQL mode detected, language selection will be locked");
        }
        
        // Reset hint usage for new interview
        const jobInterviewId = localStorage.getItem("job_interview_id");
        if (jobInterviewId) {
          // logger.debug("Resetting hint usage for new interview");
          localStorage.removeItem(`interview_hints_used_${jobInterviewId}`);
          setHintsUsed(0);
        }
        
        setsocketData({
          isNavigate: true, 
          room_url: res?.room_url, 
          user_name: res?.user_name,
          language: language, // Use the exact language from the response
          side_camera_url: res?.side_camera_url,
          isSqlMode: language.toLowerCase() === 'sql'
        });
        setProctoringEnabled(res?.proctoring_enabled);
        setInterviewCategoryWithStorage(res?.interview_category || '');
        setCodeLanguage(language); // Set the code language directly
        setIsCodeEditorEnabled(res?.interview_category === 'TECH');
        // logger.debug("Setting interview category from bot_started:", res?.interview_category);
        // logger.debug("Setting code language:", language);
      }
      if(res?.type ==="side_camera_joined"){
        logInterviewEvent('side camera joined', {
          interview_id: interviewId
        });
        setSecondryCameraJoined(true)
      }
      
     
      if (res?.type === "transcript") {
        setTranscriptData((prevMessages) => [...prevMessages, { type: res.type, message: res.message }]);
        setAiTranscript(res?.message);
      }
      if (res?.type === "bot_speaking") {
        setEvaSpeaking(true);
      }
      if (res?.type === "bot_stopped_speaking") {
        setEvaSpeaking(false);
      }
      if (res?.type === "question") {
        logInterviewEvent('question received', {
          interview_id: interviewId,
          has_timer: !!res?.timer
        });
        setQuestion(res?.message);
        setMCQComponentVisible(false);
        setShowEditor(false);
        setReactCodingQuestion(false);
        setShowExcalidraw(false);
        
        // Stop keep-alive timer when regular question starts
        stopKeepAliveTimer();
        
        // Check if timer is included in the question, and set it
        if (res?.timer) {
          // logger.debug("Setting normal question timer:", res.timer);
          const timerValue = typeof res.timer === 'string' && res.timer.includes(':') 
            ? res.timer 
            : formatTimeValue(res.timer);
          
          setQuestionTimer(timerValue);
        } else {
          setQuestionTimer(null);
        }
      }
      if (res?.type === "interview_ended") {
        logInterviewEvent('interview ended', {
          interview_id: interviewId
        });
        setsocketData({isNavigate:true, type:'ended', interview_id:id})
        
        // Stop keep-alive timer when interview ends
        stopKeepAliveTimer();
      }
      if (res?.type === "question_asked") {
        logInterviewEvent('question asked', {
          interview_id: interviewId,
          has_timer: !!res?.timer,
          remarks: res?.remarks
        });
        setTyping(res?.remarks);
        if (res?.timer) {
          // logger.debug("Setting question timer:", res.timer);
          // Ensure the timer is in the correct mm:ss format
          const timerValue = typeof res.timer === 'string' && res.timer.includes(':') 
            ? res.timer 
            : formatTimeValue(res.timer);
          
          // Clear any previous timer first
          setQuestionTimer(null);
          
          // Set the timer with a small delay to ensure state updates properly
          setTimeout(() => {
            // logger.debug("Setting timer with value:", timerValue);
            setQuestionTimer(timerValue);
          }, 50);
        } else {
          setQuestionTimer(null);
        }
      }

      if (res?.type === "hld_question") {
        // logger.debug("🧠 HLD Question Payload:", res);
        logInterviewEvent('HLD question received', {
          interview_id: interviewId,
          has_timer: !!res?.timer
        });
      
        setShowExcalidraw(false);
      
        setShowEditor(false);
        setReactCodingQuestion(false);
        setMCQComponentVisible(false);
        
        // Stop keep-alive timer when HLD question starts
        stopKeepAliveTimer();
      
        setTimeout(() => {
          if (res?.id) setId(res?.id);
          if (res?.message) setQuestion(res?.message);
          setShowExcalidraw(true);
        }, 500);
      
        if (res?.timer) {
          const timerValue = typeof res.timer === 'string' && res.timer.includes(':')
            ? res.timer
            : formatTimeValue(res.timer);
          setQuestionTimer(timerValue);
        } else {
          setQuestionTimer(null);
        }
      }

      if (res?.type === "coding_question") {
        logInterviewEvent('coding question received', {
          interview_id: interviewId,
          has_timer: !!res?.timer,
          has_boilerplate: !!res?.boilerplate_code,
          language: codeLanguage
        });
        
        // Set the code language from the question if specified
        if (res?.code_language) {
          const questionLanguage = res.code_language.toLowerCase();
          // logger.debug('Setting code language for coding question:', questionLanguage);
          setCodeLanguage(questionLanguage);
        }
        
        setShowEditor(true);
        if (res?.id) {
          setId(res?.id);
        }
        setReactCodingQuestion(false);
        setShowExcalidraw(false);
        setQuestion(res?.message);
        setTyping(res?.remarks || "");
        
        // Start keep-alive timer for coding question
        startKeepAliveTimer();
        
        // Check for timer in coding question
        if (res?.timer) {
          // logger.debug("Setting coding question timer:", res.timer);
          // Ensure the timer is in the correct mm:ss format
          const timerValue = typeof res.timer === 'string' && res.timer.includes(':') 
            ? res.timer 
            : formatTimeValue(res.timer);
          
          // Set the timer immediately without delay
          // logger.debug("Setting timer with value:", timerValue);
          setQuestionTimer(timerValue);
        } else {
          setQuestionTimer(null);
        }
        
        if (res?.boilerplate_code) {
          logger.debug('Received boilerplate code:', res.boilerplate_code);
          logger.debug('Current language setting:', codeLanguage);
          
          if (codeLanguage === 'all') {
            // If language is "all", provide a default boilerplate but allow user to change language
            const defaultLanguage = 'Java'; // Default language to show initially
            const boilerplateCode = res.boilerplate_code[defaultLanguage] || '';
            
            setEditorContent({
              boilerplate_code: res.boilerplate_code,
              current_code: boilerplateCode,
              allowLanguageSelection: true // Flag to indicate language selection should be enabled
            });
          } else {
            // Use the specific language that was set
            const keyToUse = getLanguageKeyForBoilerplate(codeLanguage);
            
            // logger.debug('Using boilerplate code for key:', keyToUse);
            logger.debug('Available keys in boilerplate:', Object.keys(res.boilerplate_code));
            
            // Try to find the best match for the boilerplate code
            const boilerplateCode = res.boilerplate_code[keyToUse] || 
                                   (codeLanguage.toLowerCase() === 'sql' ? res.boilerplate_code['SQL'] : null) ||
                                   res.boilerplate_code['Java'] || 
                                   '';
            
            logger.debug('Selected boilerplate code:', boilerplateCode ? boilerplateCode.substring(0,  50)+ '...' : 'none');
            
            setEditorContent({
              boilerplate_code: res.boilerplate_code,
              current_code: boilerplateCode,
              allowLanguageSelection: false
            });
          }
        } else {
          // No boilerplate provided
          if (codeLanguage === 'all') {
            // If language is "all", just provide a generic comment and empty editor
            // This allows the user to choose any language
            const newContent = `// ${res?.remarks}\n\n// You can code in any language of your choice`;
            setEditorContent({
              current_code: newContent,
              allowLanguageSelection: true
            });
          } else {
            // Use the specific language snippet
            const newContent = `// ${res?.remarks}\n\n${CODE_SNIPPETS[codeLanguage] || CODE_SNIPPETS.java}`;
            setEditorContent({
              current_code: newContent,
              allowLanguageSelection: false
            });
          }
        }
      }
      if (res?.type === "react_coding_question") {
        logInterviewEvent('React coding question received', {
          interview_id: interviewId,
          has_timer: !!res?.timer,
          has_boilerplate: !!res?.boilerplate_code
        });
        setShowEditor(true);
        if (res?.id) {
          setId(res?.id);
        }
        setBoilerplateCode(res?.boilerplate_code);
        setReactCodingQuestion(true);
        setQuestion(res?.message);
        setTyping(res?.remarks || "");
        
        // Start keep-alive timer for React coding question
        console.log('🚀 Starting keep-alive timer for React coding question');
        startKeepAliveTimer();
        
        // Check for timer in React coding question
        if (res?.timer) {
          // logger.debug("Setting React coding question timer:", res.timer);
          // Ensure the timer is in the correct mm:ss format
          const timerValue = typeof res.timer === 'string' && res.timer.includes(':') 
            ? res.timer 
            : formatTimeValue(res.timer);
          
          // Clear any previous timer first
          setQuestionTimer(null);
          
          // Set the timer with a small delay to ensure state updates properly
          setTimeout(() => {
            // logger.debug("Setting React coding timer with value:", timerValue);
            setQuestionTimer(timerValue);
          }, 50);
        } else {
          setQuestionTimer(null);
        }
      }
      if(res?.type==="mcq_question"){
        logInterviewEvent('MCQ question received', {
          interview_id: interviewId,
          has_options: !!res?.options
        });
        setMCQComponentVisible(true);
        setMCQDataAvailable(res);
        
        // Stop keep-alive timer when MCQ question starts
        stopKeepAliveTimer();
      }
      
      if (res?.type === "skip_question") {
        logInterviewEvent('question skipped by system', {
          interview_id: interviewId,
          message: res.message
        });
        toast(res.message);
      }
      if (res?.type === "explanation_started") {
        logInterviewEvent('explanation requested by system', {
          interview_id: interviewId
        });
        toast("Please explain your code");
      }
      if (res?.type === "question_skipped") {
        logInterviewEvent('question skipped confirmed', {
          interview_id: interviewId
        });
        setShowEditor(false);
        setShowExcalidraw(false);
        setQuestion('');
        
        // Stop keep-alive timer when question is skipped
        stopKeepAliveTimer();
      }
      if (res?.type === "code_submitted") {
        logInterviewEvent('code submitted successfully', {
          interview_id: interviewId
        });
        toast.success("Code submitted successfully!");
        
        // Stop keep-alive timer when code is submitted
        stopKeepAliveTimer();
      }
      if (res?.type === "explaination_started") {
        logInterviewEvent('explanation started', {
          interview_id: interviewId
        });
        toast("Please explain your code");
      }
      if(res?.type==="approach_hint"){
        logInterviewEvent('approach hint received', {
          interview_id: interviewId,
          hint_message: res?.message
        });
        toast(res?.message);
      }
      if(res?.type==="debug_hint"){
        logInterviewEvent('debug hint received', {
          interview_id: interviewId,
          hint_message: res?.message
        });
        toast(res?.message);
      }
      if(res?.type==="entity_detection_failure"){

        logger.debug('⚠️ Entity Detection Failure Event Received:', {

          type: res.type,
          fullData: res
        });
        logInterviewEvent('entity detection failure', {
          interview_id: interviewId
        });
        setShowEntityDetectionPopup(true);
      }
    };

    ws.onerror = (error) => {
      // logger.error("WebSocket error:", error);
      logWebSocketError(error, {
        tags: { action: 'websocket_error' },
        extra: { interview_id: interviewId }
      });
    };

      ws.onclose = (event) => {
        // logger.debug("WebSocket connection closed", event.code, event.reason);
        logWebSocketEvent('connection closed', {
          interview_id: interviewId,
          code: event.code,
          reason: event.reason,
          was_clean: event.wasClean
        });
        setIsConnected(false);
        
        // Queue disconnection event since we can't send directly
        queueConnectionEvent("websocket_status", {
          type: "client_event",
          message: "websocket_disconnected",
          timestamp: new Date().toISOString()
        });
        
        // Clear the permission check interval when connection closes
        if (permissionCheckInterval) {
          clearInterval(permissionCheckInterval);
        }
        
        // Attempt to reconnect if not a normal closure and within max attempts
        if (event.code !== 1000 && reconnectAttempts < maxReconnectAttempts) {
          const nextAttempt = reconnectAttempts + 1;
          const delay = Math.min(1000 * Math.pow(2, nextAttempt), 30000); // Exponential backoff with 30s max
          
          // logger.debug(`Attempting to reconnect (${nextAttempt}/${maxReconnectAttempts}) in ${delay}ms...`);
          logWebSocketEvent('reconnection attempt', {
            interview_id: interviewId,
            attempt: nextAttempt,
            maxAttempts: maxReconnectAttempts,
            delay: delay
          });
          setReconnectAttempts(nextAttempt);
          if (reconnectionStatus !== 'loading') { 
            setReconnectionStatus('connecting');
          }
          
          // Show toast notification about reconnection attempt
          toast.loading(`Connection lost. Reconnecting (${nextAttempt}/${maxReconnectAttempts})...`, {
            id: 'reconnection-toast',
            duration: delay
          });
          if (reconnectionStatus !== 'loading') { 
            setReconnectionStatus('connecting');
          }
          
          reconnectTimeoutRef.current = setTimeout(() => {
            connectWebSocket();
          }, delay);
        } else if (reconnectAttempts >= maxReconnectAttempts) {
          // Max attempts reached
          logWebSocketEvent('max reconnection attempts reached', {
            interview_id: interviewId,
            totalAttempts: reconnectAttempts
          });
          setReconnectionStatus('max_attempts_reached');
          toast.error('Maximum reconnection attempts reached. Please try manually reconnecting.', {
            id: 'reconnection-toast',
            duration: 5000
          });
        }
      };

    setWs(ws);

    // Add visibility change handler
    const handleVisibilityChange = () => {
      const isVisible = !document.hidden;
      setIsTabVisible(isVisible);
      
      if (ws && ws.readyState === WebSocket.OPEN) {
        ws.send(JSON.stringify({
          type: "client_event",
          message: "tab_switched",
        }));
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    handlePermissionChange();

    // Add fullscreen change handler
    const handleFullscreenChange = () => {
      if (!document.fullscreenElement && 
          !document.webkitFullscreenElement && 
          !document.mozFullScreenElement) {
        // User attempted to exit fullscreen
        sendFullscreenExitEvent();
      }
    };

    // Add event listeners for different browsers
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
    document.addEventListener('mozfullscreenchange', handleFullscreenChange);
    document.addEventListener('MSFullscreenChange', handleFullscreenChange);

    // Add page refresh/unload handler
    const handleBeforeUnload = (event) => {
      // Standard beforeunload handling
      event.preventDefault();
      event.returnValue = "";
    };

    // Use a flag to track if we're refreshing the page
    let isRefreshing = false;

    // Handle actual page refresh
    const handleRefresh = () => {
      isRefreshing = true;
      if (ws && ws.readyState === WebSocket.OPEN) {
        // Send refresh event
        ws.send(JSON.stringify({
          type: "client_event",
          message: "page_refreshed/Tab_closed"
        }));
      }
    };

    // Add pagehide event handler to detect tab close
    const handlePageHide = (event) => {
      // Only send tab_close if we're not refreshing
      if (!isRefreshing && ws && ws.readyState === WebSocket.OPEN) {
        sendTabCloseEvent();
      }
    };

    // Add beforeunload event listener for standard handling
    window.addEventListener("beforeunload", handleBeforeUnload);
    // Add specific event for refresh detection
    window.addEventListener("beforeunload", handleRefresh);
    // Add pagehide event listener for tab close detection
    window.addEventListener("pagehide", handlePageHide);

    handlePermissionChange();

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      // Clean up event listeners
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
      document.removeEventListener('mozfullscreenchange', handleFullscreenChange);
      document.removeEventListener('MSFullscreenChange', handleFullscreenChange);
      window.removeEventListener("beforeunload", handleBeforeUnload);
      window.removeEventListener("beforeunload", handleRefresh);
      window.removeEventListener("pagehide", handlePageHide);
      // Clear interval and close websocket when component unmounts
      if (permissionCheckInterval) {
        clearInterval(permissionCheckInterval);
      }
      if (ws) {
        ws.close();
      }
      if (permissionChangeTimeout.current) {
        clearTimeout(permissionChangeTimeout.current);
      }
      // Clear keep-alive timer when component unmounts
      if (keepAliveIntervalRef.current) {
        clearInterval(keepAliveIntervalRef.current);
      }
    };
  }, [])

 
  const sendExplanationRequest = () => {
    // logger.debug('Attempting to send explanation request...');
    logInterviewEvent('explanation request initiated', {
      interview_id: interviewId,
      websocket_connected: ws?.readyState === WebSocket.OPEN
    });
    if (ws && ws.readyState === WebSocket.OPEN ) {
      const message = {
        
        type: "coding_event",
        message: "explaination_started"
      };
      // logger.debug('Sending explanation request:', message);
      ws.send(JSON.stringify(message));
      toast.success('Explanation requested');
      logInterviewEvent('explanation request sent', {
        interview_id: interviewId
      });
    } else {
      // logger.warn('WebSocket is not connected for explanation or missing interview ID. Current state:', ws?.readyState);
      logWebSocketError(new Error('WebSocket not connected for explanation request'), {
        tags: { action: 'explanation_request' },
        extra: { 
          interview_id: interviewId,
          websocket_state: ws?.readyState 
        }
      });
      toast.error('Not connected to server or missing interview ID');
    }
  };

  const sendSkipRequest = (props) => {
    // logger.debug('Attempting to send skip request...');
    logInterviewEvent('skip request initiated', {
      interview_id: interviewId,
      websocket_connected: ws?.readyState === WebSocket.OPEN,
      is_submitted: props?.isSubmitted
    });
    if (ws && ws.readyState === WebSocket.OPEN ) {
      const message = {
        
        type: "coding_event",
        message: "skip_question"
      };
      // logger.debug('Sending skip request:', message);
      ws.send(JSON.stringify(message));
      toast.success('Skip requested');
      logInterviewEvent('skip request sent', {
        interview_id: interviewId
      });
      if (props?.isSubmitted) {
        setMCQComponentVisible(false);
      }
    } else {
      // logger.warn('WebSocket is not connected for skip or missing interview ID. Current state:', ws?.readyState);
      logWebSocketError(new Error('WebSocket not connected for skip request'), {
        tags: { action: 'skip_request' },
        extra: { 
          interview_id: interviewId,
          websocket_state: ws?.readyState 
        }
      });
      toast.error('Not connected to server or missing interview ID');
    }
  };

  const sendDebugHintRequest = (code) => {
    // logger.debug('Attempting to send debug hint request...');
    const jobInterviewId = localStorage.getItem("job_interview_id");
    
    // Get the latest hint count from localStorage
    const currentHintCount = (() => {
      const savedHints = localStorage.getItem(`interview_hints_used_${jobInterviewId}`);
      return savedHints ? parseInt(savedHints, 10) : hintsUsed;
    })();
    
    logInterviewEvent('debug hint request initiated', {
      interview_id: jobInterviewId,
      current_hint_count: currentHintCount,
      max_hints: MAX_HINTS_PER_INTERVIEW,
      websocket_connected: ws?.readyState === WebSocket.OPEN
    });
    
    if (ws && ws.readyState === WebSocket.OPEN && currentHintCount < MAX_HINTS_PER_INTERVIEW) {
      const message = {
        type: "coding_event",
        message: "debug_hint",
        code: code
      };
      // logger.debug('Sending debug hint request:', message);
      ws.send(JSON.stringify(message));
      
      // Increment hint count and save to localStorage with interview-specific key
      const newHintCount = currentHintCount + 1;
      setHintsUsed(newHintCount);
      localStorage.setItem(`interview_hints_used_${jobInterviewId}`, newHintCount.toString());
      // logger.debug(`Updated hint count for interview ${jobInterviewId}: ${newHintCount}`);
      
      toast.success('Debug hint requested');
      logInterviewEvent('debug hint request sent', {
        interview_id: jobInterviewId,
        new_hint_count: newHintCount
      });
    } else if (currentHintCount >= MAX_HINTS_PER_INTERVIEW) {
      logInterviewEvent('debug hint request denied - limit reached', {
        interview_id: jobInterviewId,
        current_hint_count: currentHintCount,
        max_hints: MAX_HINTS_PER_INTERVIEW
      });
      toast.error('You have reached the maximum limit of 3 hints per interview');
    } else {
      // logger.warn('WebSocket is not connected for debug hint. Current state:', ws?.readyState);
      logWebSocketError(new Error('WebSocket not connected for debug hint request'), {
        tags: { action: 'debug_hint_request' },
        extra: { 
          interview_id: jobInterviewId,
          websocket_state: ws?.readyState 
        }
      });
      toast.error('Not connected to server');
    }
  };

  const sendApproachHintRequest = (code) => {
    // logger.debug('Attempting to send approach hint request...');
    const jobInterviewId = localStorage.getItem("job_interview_id");
    
    // Get the latest hint count from localStorage
    const currentHintCount = (() => {
      const savedHints = localStorage.getItem(`interview_hints_used_${jobInterviewId}`);
      return savedHints ? parseInt(savedHints, 10) : hintsUsed;
    })();
    
    logInterviewEvent('approach hint request initiated', {
      interview_id: jobInterviewId,
      current_hint_count: currentHintCount,
      max_hints: MAX_HINTS_PER_INTERVIEW,
      websocket_connected: ws?.readyState === WebSocket.OPEN
    });
    
    if (ws && ws.readyState === WebSocket.OPEN && currentHintCount < MAX_HINTS_PER_INTERVIEW) {
      const message = {
        type: "coding_event",
        message: "approach_hint",
        code: code
      };
      // logger.debug('Sending approach hint request:', message);
      ws.send(JSON.stringify(message));
      
      // Increment hint count and save to localStorage with interview-specific key
      const newHintCount = currentHintCount + 1;
      setHintsUsed(newHintCount);
      localStorage.setItem(`interview_hints_used_${jobInterviewId}`, newHintCount.toString());
      // logger.debug(`Updated hint count for interview ${jobInterviewId}: ${newHintCount}`);
      
      toast.success('Approach hint requested');
      logInterviewEvent('approach hint request sent', {
        interview_id: jobInterviewId,
        new_hint_count: newHintCount
      });
    } else if (currentHintCount >= MAX_HINTS_PER_INTERVIEW) {
      logInterviewEvent('approach hint request denied - limit reached', {
        interview_id: jobInterviewId,
        current_hint_count: currentHintCount,
        max_hints: MAX_HINTS_PER_INTERVIEW
      });
      toast.error('You have reached the maximum limit of 3 hints per interview');
    } else {
      // logger.warn('WebSocket is not connected for approach hint. Current state:', ws?.readyState);
      logWebSocketError(new Error('WebSocket not connected for approach hint request'), {
        tags: { action: 'approach_hint_request' },
        extra: { 
          interview_id: jobInterviewId,
          websocket_state: ws?.readyState 
        }
      });
      toast.error('Not connected to server');
    }
  };

  const surroundingRecorded = (code) => {
    // logger.debug('surrounding recorded...');
    logInterviewEvent('surrounding recording initiated', {
      interview_id: interviewId,
      websocket_connected: ws?.readyState === WebSocket.OPEN
    });
    if (ws && ws.readyState === WebSocket.OPEN) {
      const message = {
        type: "surroundings_recorded",
      };
      // logger.debug('Sending surrounding recorded:', message);
      ws.send(JSON.stringify(message));
      toast.success('Surrounding Recorded');
      logInterviewEvent('surrounding recording event sent', {
        interview_id: interviewId
      });
    } else {
      // logger.warn('WebSocket is not connected for surrounding recorded. Current state:', ws?.readyState);
      logWebSocketError(new Error('WebSocket not connected for surrounding recorded'), {
        tags: { action: 'surrounding_recorded' },
        extra: { 
          interview_id: interviewId,
          websocket_state: ws?.readyState 
        }
      });
      toast.error('Not connected to server');
    }
  };

  const cameraPlaced = (code) => {
    // logger.debug('camera placed...');
    logInterviewEvent('camera placement initiated', {
      interview_id: interviewId,
      websocket_connected: ws?.readyState === WebSocket.OPEN
    });
    if (ws && ws.readyState === WebSocket.OPEN) {
      const message = {
        type: "camera_placed",
      };
      // logger.debug('Sending camera placed:', message);
      ws.send(JSON.stringify(message));
      // Store camera placement status in localStorage with key name
      localStorage.setItem('camera_placed', 'true');
      toast.success('Thank You! Best of luck');
      logInterviewEvent('camera placement event sent', {
        interview_id: interviewId
      });
    } else {
      // logger.warn('WebSocket is not connected for camera placed. Current state:', ws?.readyState);
      logWebSocketError(new Error('WebSocket not connected for camera placed'), {
        tags: { action: 'camera_placed' },
        extra: { 
          interview_id: interviewId,
          websocket_state: ws?.readyState 
        }
      });
      toast.error('Not connected to server');
    }
  };

  // Improve the internet connection detection
  useEffect(() => {
    const handleOnline = () => {
      // logger.debug('Internet connection restored');
      logNetworkEvent('connection restored', {
        interview_id: interviewId
      });
      setIsOnline(true);
      toast.success('Internet connection restored. Refreshing page...');
      
      // Queue online event
      queueConnectionEvent("internet_status", {
        type: "client_event",
        message: "internet_connection_status",
        status: "online",
        timestamp: new Date().toISOString()
      });
      
      // Attempt to reconnect WebSocket if it was disconnected
      if (ws && ws.readyState !== WebSocket.OPEN) {
        // logger.debug('Attempting to reconnect WebSocket after internet restored');
        logWebSocketEvent('reconnecting after internet restoration', {
          interview_id: interviewId
        });
        // Reset reconnection attempts when internet is restored
        setReconnectAttempts(0);
        setReconnectionStatus('connecting');
        connectWebSocket();
      }
    };

    const handleOffline = () => {
      // logger.debug('Internet connection lost');
      logNetworkEvent('connection lost', {
        interview_id: interviewId,
        websocket_state: ws?.readyState
      });
      setIsOnline(false);
      
      // Try to send the disconnection event
      if (ws && ws.readyState === WebSocket.OPEN) {
        try {
          ws.send(JSON.stringify({
            type: "client_event",
            message: "internet_disconnected"
          }));
          // logger.debug('Internet disconnection event sent successfully');
          logNetworkEvent('disconnection event sent', {
            interview_id: interviewId
          });
        } catch (error) {
          // logger.error("Error sending internet_disconnected event:", error);
          logNetworkError(error, {
            tags: { action: 'send_disconnection_event' },
            extra: { interview_id: interviewId }
          });
          
          // Queue the event if sending fails
          queueConnectionEvent("internet_status", {
            type: "client_event",
            message: "internet_disconnected",
            timestamp: new Date().toISOString()
          });
        }
      } else {
        // Queue the event if WebSocket is not connected
        queueConnectionEvent("internet_status", {
          type: "client_event",
          message: "internet_disconnected",
          timestamp: new Date().toISOString()
        });
      }
      
      toast.error('Internet connection lost. Please check your connection.');
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Initial check
    setIsOnline(navigator.onLine);
    logNetworkEvent('initial connection check', {
      interview_id: interviewId,
      is_online: navigator.onLine
    });

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [ws, connectWebSocket]);

  // Function to start keep-alive timer for coding questions
  const startKeepAliveTimer = useCallback(() => {
    // Clear any existing timer first
    if (keepAliveIntervalRef.current) {
      clearInterval(keepAliveIntervalRef.current);
    }
    
    // Start new timer that sends keep-alive every 30 seconds
    keepAliveIntervalRef.current = setInterval(() => {
      if (ws && ws.readyState === WebSocket.OPEN) {
        const keepAliveMessage = {
          type: "keep_alive",
          message: "PING"
        };
        ws.send(JSON.stringify(keepAliveMessage));
        logWebSocketEvent('keep-alive sent', {
          interview_id: interviewId,
          question_type: reactCodingQuestion ? 'react_coding' : (showEditor ? 'coding' : 'unknown')
        });
        
        // Debug log for React coding questions
        if (reactCodingQuestion) {
          console.log('📡 Keep-alive PING sent for React coding question', keepAliveMessage);
        }
      }
    }, 30000); // 30 seconds
    
    logWebSocketEvent('keep-alive timer started', {
      interview_id: interviewId,
      interval: 30000,
      question_type: reactCodingQuestion ? 'react_coding' : (showEditor ? 'coding' : 'unknown')
    });
    
    // Debug log for React coding questions
    if (reactCodingQuestion) {
      console.log('🔄 Keep-alive timer started for React coding question');
    }
  }, [ws, interviewId, showEditor, reactCodingQuestion]);

  // Function to stop keep-alive timer
  const stopKeepAliveTimer = useCallback(() => {
    if (keepAliveIntervalRef.current) {
      clearInterval(keepAliveIntervalRef.current);
      keepAliveIntervalRef.current = null;
      logWebSocketEvent('keep-alive timer stopped', {
        interview_id: interviewId
      });
    }
  }, [interviewId]);

  // Helper function to format time values consistently
  const formatTimeValue = (value) => {
    if (!value) return null;
    
    // If it's already a string in mm:ss format, return it
    if (typeof value === 'string' && value.includes(':')) {
      return value;
    }
    
    // If it's a number or can be parsed as a number, treat as minutes
    const minutes = parseInt(value, 10);
    if (!isNaN(minutes)) {
      return `${minutes}:00`;
    }
    
    return null;
  };

  // Monitor WebSocket connection status changes and send updates
  useEffect(() => {
    // Only run if WebSocket exists
    if (ws) {
      // Send connection status whenever isConnected changes
      sendConnectionStatusEvent(isConnected ? 'connected' : 'disconnected');
      
      // logger.debug(`WebSocket connection status changed: ${isConnected ? "connected" : "disconnected"}`);
    }
  }, [isConnected, ws]);

  const callEnded = () => {
    // logger.debug('Attempting to send call_ended event...');
    logInterviewEvent('call end requested', {
      interview_id: interviewId,
      websocket_connected: ws?.readyState === WebSocket.OPEN
    });
    if (ws && ws.readyState === WebSocket.OPEN) {
      const message = {
        type: "call_ended"
      };
      // logger.debug('Sending call_ended event:', message);
      ws.send(JSON.stringify(message));
      toast.success('Call ended');
      logInterviewEvent('call end event sent', {
        interview_id: interviewId
      });
    } else {
      // logger.warn('WebSocket is not connected for call_ended. Current state:', ws?.readyState);
      logWebSocketError(new Error('WebSocket not connected for call_ended'), {
        tags: { action: 'call_ended' },
        extra: { 
          interview_id: interviewId,
          websocket_state: ws?.readyState 
        }
      });
      toast.error('Not connected to server');
    }
  };

  // Function to reset hint usage for the current interview
  const resetHintUsage = () => {
    // logger.debug('Resetting hint usage for current interview');
    const jobInterviewId = localStorage.getItem("job_interview_id");
    logInterviewEvent('hint usage reset requested', {
      interview_id: jobInterviewId
    });
    if (jobInterviewId) {
      localStorage.removeItem(`interview_hints_used_${jobInterviewId}`);
      setHintsUsed(0);
      toast.success('Hint usage reset for this interview');
      logInterviewEvent('hint usage reset successful', {
        interview_id: jobInterviewId
      });
    } else {
      logInterviewError(new Error('Unable to reset hints: No interview ID found'), {
        tags: { action: 'reset_hint_usage' }
      });
      toast.error('Unable to reset hints: No interview ID found');
    }
  };

  // Replace with simpler effect that only processes regular pending events
  useEffect(() => {
    if (isConnected && ws && ws.readyState === WebSocket.OPEN) {
      // Small delay to ensure connection is fully established
      setTimeout(() => {
        // Process pending events
        if (pendingEvents.length > 0) {
          // logger.debug(`Processing ${pendingEvents.length} pending events`);
          pendingEvents.forEach(event => {
            if (ws && ws.readyState === WebSocket.OPEN) {
              ws.send(JSON.stringify(event.data));
            }
          });
          setPendingEvents([]);
        }
      }, 300);
    } else if (!isConnected) {
      // Queue disconnection event when disconnected
      queueConnectionEvent("websocket_status", {
        type: "client_event",
        message: "websocket_disconnected",
        timestamp: new Date().toISOString()
      });
    }
  }, [isConnected, ws, pendingEvents]);

  // Update the hint count retrieval whenever the job_interview_id changes
  useEffect(() => {
    const updateHintCount = () => {
      const jobInterviewId = localStorage.getItem("job_interview_id");
      if (jobInterviewId) {
        const savedHints = localStorage.getItem(`interview_hints_used_${jobInterviewId}`);
        if (savedHints) {
          const parsedHints = parseInt(savedHints, 10);
          // logger.debug(`Loaded hint count for interview ${jobInterviewId}: ${parsedHints}`);
          setHintsUsed(parsedHints);
        }
      }
    };
    
    // Call immediately and also set up an event listener for localStorage changes
    updateHintCount();
    
    // Set up a storage event listener to keep hint count in sync across tabs
    const handleStorageChange = (e) => {
      const jobInterviewId = localStorage.getItem("job_interview_id");
      if (e.key === `interview_hints_used_${jobInterviewId}`) {
        updateHintCount();
      }
    };
    
    window.addEventListener('storage', handleStorageChange);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  // Update the function to store in localStorage and update dependent states
  const setInterviewCategoryWithStorage = (category) => {
    // logger.debug("Setting interview category in storage:", category);
    localStorage.setItem("interviewCategory", category);
    setInterviewCategory(category);
    
    // Also update the isCodeEditorEnabled state based on the category
    if (category === 'TECH') {
      setIsCodeEditorEnabled(true);
      // logger.debug("Enabled code editor based on TECH category");
    } else {
      setIsCodeEditorEnabled(false);
      // logger.debug("Disabled code editor based on non-TECH category");
    }
  };

  // Add a function to handle proctoring events and relay them to the server
  const handleProctoringEvent = useCallback((eventType, data = {}) => {
    if (ws && ws.readyState === WebSocket.OPEN) {
      const message = {
        type: "client_event",
        message: eventType,
        ...data,
        timestamp: new Date().toISOString()
      };
      
      // logger.debug(`Sending proctoring event to server: ${eventType}`);
      ws.send(JSON.stringify(message));
      
      // Update local state tracking
      setProctoringStepsCompleted(prev => {
        const newState = { ...prev };
        
        // Update appropriate flag based on event type
        if (eventType === "proctoring_qr_scan_complete") {
          newState.qrScanned = true;
        } else if (eventType === "proctoring_secondary_device_joined") {
          newState.deviceJoined = true;
        } else if (eventType === "proctoring_360_scan_complete") {
          newState.roomScanned = true;
        } else if (eventType === "proctoring_camera_placement_complete") {
          newState.cameraPlaced = true;
        }
        
        return newState;
      });
      
      return true;
    } else {
      // logger.warn(`WebSocket not connected for sending ${eventType} event`);
      return false;
    }
  }, [ws]);

  // Function to handle app messages from Daily.co video call
  const handleDailyAppMessage = useCallback((event) => {
    const { data } = event;
    
    // Check if this is a proctoring event from the secondary device
    if (data && data.type === 'proctoring_event') {
      // logger.debug('Received proctoring event from secondary device:', data);
      
      switch (data.event) {
        case 'secondary_camera_joined':
          handleProctoringEvent('proctoring_secondary_device_joined');
          setSecondryCameraJoined(true);
          break;
        
        case 'secondary_camera_video_active':
          handleProctoringEvent('proctoring_secondary_camera_active', {
            using_fallback: data.using_fallback || false
          });
          break;
        
        case '360_scan_started':
          handleProctoringEvent('proctoring_360_scan_started');
          break;
        
        case '360_scan_completed':
          handleProctoringEvent('proctoring_360_scan_complete');
          break;
        
        default:
          // logger.debug('Unknown proctoring event:', data.event);
      }
    }
  }, [handleProctoringEvent]);

  // Listen for Daily.co app messages for cross-device communication
  useEffect(() => {
    // Set up global event listener for Daily app messages
    window.addEventListener('message', (event) => {
      // Check if the message is from Daily.co
      if (event.data && typeof event.data === 'object' && event.data.fromDaily) {
        // Check if it's a participant app message (custom event from other devices)
        if (event.data.type === 'app-message') {
          handleDailyAppMessage(event.data);
        }
      }
    });
    
    return () => {
      // Clean up event listener if needed
      // Note: This may not fully work for message events, but it's good practice
      window.removeEventListener('message', handleDailyAppMessage);
    };
  }, [handleDailyAppMessage]);

  // Carousel auto-rotation effect for mobile placement images
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentImageIndex((prevIndex) => (prevIndex + 1) % 2);
    }, 3000); // Change image every 3 seconds

    return () => clearInterval(interval);
  }, []);

  // Add a function to send proctoring setup completion event
  const sendProctoringSetupComplete = () => {
    if (ws && ws.readyState === WebSocket.OPEN) {
      const message = {
        type: "client_event",
        message: "proctoring_setup_complete",
        steps_completed: proctoringStepsCompleted,
        timestamp: new Date().toISOString()
      };
      
      // logger.debug('Sending proctoring setup complete event to server');
      ws.send(JSON.stringify(message));
      return true;
    } else {
      // logger.warn('WebSocket not connected for sending proctoring completion event');
      return false;
    }
  };

  return (
    <WebSocketContext.Provider
      value={{
        socketData,
        evaSpeaking,
        transcriptData,
        aiTranscript,
        question,
        showEditor,
        typing,
        editorContent,
        setEditorContent,
        interviewId,
        id,
        isTabVisible,
        interviewCategory,
        isCodeEditorEnabled,
        isConnected,
        prevCameraState,
        prevMicState,
        ws,
        sendPermissionChangeEvent,
        sendScreenshotEvent,
        sendFullscreenExitEvent,
        sendTabCloseEvent,
        queueConnectionEvent,
        sendConnectionStatusEvent,
        // Add direct permission event functions
        sendCameraPermissionSuccess,
        sendMicrophonePermissionSuccess,
        sendBotInteractionSuccess,
        reactCodingQuestion,
        codeLanguage,
        isProctoringEnabled,
        isSecondryCameraJoined,
        questionTimer,
        callEnded,
        isMCQComponentVisible,
        isMCQDataAvailable,
        currentQuestionIndex,
        totalQuestions,
        isOnline,
        reconnectAttempts,
        maxReconnectAttempts,
        reconnectionStatus,
        manualRetryConnection,
        handleSubmit,
        setCodeLanguage,
        setTyping,
        setQuestion,
        setQuestionTimer,
        setEditorContent,
        setShowEditor,
        setProctoringEnabled,
        setSecondryCameraJoined,
        sendSkipRequest,
        sendExplanationRequest,
        sendDebugHintRequest,
        sendApproachHintRequest,
        surroundingRecorded,
        cameraPlaced,
        callEnded,
        hintsUsed,
        MAX_HINTS_PER_INTERVIEW,
        setMCQComponentVisible,
        setMCQDataAvailable,
        setCurrentQuestionIndex,
        setTotalQuestions,
        setHintsUsed,
        resetHintUsage,
        setInterviewCategory: setInterviewCategoryWithStorage,
        showExcalidraw,
        setShowExcalidraw,
        sendNoFaceDetectedEvent,
        sendMultipleFacesDetectedEvent,
        proctoringStepsCompleted,
        handleProctoringEvent,
        sendProctoringSetupComplete,
        boilerplateCode,
        isProctoringResetup,
        wsMessageEvent,
        showEntityDetectionPopup,
        setShowEntityDetectionPopup,
        // Keep-alive timer functions
        startKeepAliveTimer,
        stopKeepAliveTimer
      }}
    >
      {children}
      
      {/* Entity Detection Failure Popup */}
      {showEntityDetectionPopup && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            backdropFilter: 'blur(5px)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 1000000,
            fontFamily: 'Inter, sans-serif'
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <div
            style={{
              background: 'rgba(255, 255, 255, 0.95)',
              padding: '30px 40px',
              borderRadius: '20px',
              boxShadow: '0 10px 30px rgba(0, 0, 0, 0.2)',
              maxWidth: '500px',
              width: '90%',
              textAlign: 'center',
              animation: 'modalPop 0.3s ease-out forwards'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Warning Icon */}
            <div style={{ marginBottom: '20px' }}>
              <svg
                width="50"
                height="50"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                style={{ margin: '0 auto', display: 'block' }}
              >
                <path
                  d="M12 9v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  stroke="#f44336"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
            
            {/* Warning Title */}
            <h2 style={{
              color: '#f44336',
              fontSize: '24px',
              margin: '0 0 15px',
              fontWeight: '600'
            }}>
              Warning!
            </h2>
            
            {/* Warning Message */}
            <p style={{
              color: '#4a4a4a',
              fontSize: '16px',
              lineHeight: '1.5',
              margin: '0 0 25px'
            }}>
              Please ensure your laptop screen, face, and hands are clearly visible in the mobile camera.
            </p>
            
            {/* Mobile Placement Image Carousel */}
            <div style={{ margin: '20px 0', position: 'relative' }}>
              <img
                src={currentImageIndex === 0 ? placeLaptop : placeMobile}
                alt="Mobile camera placement guide"
                style={{
                  maxWidth: '100%',
                  height: 'auto',
                  borderRadius: '12px',
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                  transition: 'opacity 0.5s ease-in-out'
                }}
              />
              
              {/* Carousel indicators */}
              <div style={{
                position: 'absolute',
                bottom: '10px',
                left: '50%',
                transform: 'translateX(-50%)',
                display: 'flex',
                gap: '8px'
              }}>
                <div style={{
                  width: '8px',
                  height: '8px',
                  borderRadius: '50%',
                  backgroundColor: currentImageIndex === 0 ? '#ffffff' : 'rgba(255, 255, 255, 0.5)',
                  transition: 'background-color 0.3s ease'
                }}></div>
                <div style={{
                  width: '8px',
                  height: '8px',
                  borderRadius: '50%',
                  backgroundColor: currentImageIndex === 1 ? '#ffffff' : 'rgba(255, 255, 255, 0.5)',
                  transition: 'background-color 0.3s ease'
                }}></div>
              </div>
            </div>
            
            {/* OK Button */}
            <button
              onClick={() => setShowEntityDetectionPopup(false)}
              style={{
                padding: '12px 32px',
                backgroundColor: '#f44336',
                color: 'white',
                border: 'none',
                borderRadius: '12px',
                fontSize: '16px',
                fontWeight: '500',
                cursor: 'pointer',
                boxShadow: '0 4px 12px rgba(244, 67, 54, 0.2)',
                transition: 'all 0.2s ease',
                minWidth: '120px'
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = '#d32f2f';
                e.target.style.transform = 'translateY(-2px)';
                e.target.style.boxShadow = '0 6px 16px rgba(244, 67, 54, 0.3)';
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = '#f44336';
                e.target.style.transform = 'translateY(0)';
                e.target.style.boxShadow = '0 4px 12px rgba(244, 67, 54, 0.2)';
              }}
            >
              OK
            </button>
          </div>
        </div>
      )}
      
      <style>
        {`
          @keyframes modalPop {
            0% { transform: scale(0.9); opacity: 0; }
            100% { transform: scale(1); opacity: 1; }
          }
        `}
      </style>
    </WebSocketContext.Provider>
  )
};
export default WebSocketProvider